/**
 * Chained automation evaluation.
 *
 * A field filled by one rule type must trigger the rule types consuming it.
 * The cascade is driven by an explicit queue over the rule types, never by
 * letting form value changes recurse, and lives here as a pure engine: it
 * knows nothing about forms, controls or screens. Each screen keeps only its
 * own concerns (target resolution, value coercion, patching) and supplies them
 * through `AutomationCascadeHost`.
 */

import { AutomationRuleTypeGroup } from '@models/automation';

/**
 * Rule types of an instance seen as a dependency graph. An output field
 * belongs to exactly one rule type, so "output field -> consuming rule types"
 * is unambiguous and the graph can be built once when the rules load.
 */
export interface AutomationCascadeGraph {
    /** Rule type consuming a field as an input, by field id. */
    consumersOf: Map<number, number[]>;
    /** Rule type ids in dependency order; empty when the graph is cyclic. */
    order: number[];
    /**
     * True when no topological order exists. Only reachable for rules
     * inserted directly into the database — the configuration screens prevent
     * loops — and it disables the cascade instead of looping the browser.
     */
    isCyclic: boolean;
}

/** Value the cascade computed for a field, ready to be written to a form. */
export interface AutomationCascadeWrite {
    fieldId: number;
    /** Rule value as configured, before coercion. */
    ruleValue: string;
    /** Value the screen's control takes, as returned by `prepareWrite`. */
    controlValue: unknown;
}

/** Coerced value of a field together with its canonical (comparable) form. */
export interface AutomationPreparedWrite {
    controlValue: unknown;
    /** Value as the rules express it, so it can be compared type-aware. */
    canonical: string | null;
}

/** The screen-specific behaviour the cascade needs. */
export interface AutomationCascadeHost {
    /** Rules of one rule type matched against the current values. */
    matchRules(
        group: AutomationRuleTypeGroup,
        currentValues: Map<number, string | null>,
    ): Map<number, string> | null;
    /**
     * Rule value coerced for the field's control, or `null` when it cannot be
     * used (unknown field, unselectable option, invalid date, ...) and the
     * current value must be kept.
     */
    prepareWrite(
        fieldId: number,
        ruleValue: string,
    ): AutomationPreparedWrite | null;
    /**
     * True when the user edited the field manually. Its write is skipped, but
     * the cascade continues from its current value so the rest of the chain is
     * still derived.
     */
    isWriteSuppressed?(fieldId: number): boolean;
    /**
     * Field ids the screen renders with the same control as `fieldId`,
     * including it. Rule types declare their own field for the same standard
     * kind or column, so a value produced for one of them is a value for all
     * of them and has to feed the rule types consuming any of them. Defaults
     * to `fieldId` alone.
     */
    fieldIdsSharingTarget?(fieldId: number): number[];
}

export interface AutomationCascadeParams {
    groups: AutomationRuleTypeGroup[];
    graph: AutomationCascadeGraph;
    /**
     * Field(s) the user (or a previous evaluation) changed. Several ids are
     * passed when one control feeds rule types that declare their own field
     * for the same standard kind; an empty list only runs the input-less rule
     * types.
     */
    changedFieldId: number | number[];
    /**
     * Canonical current value per field id. A field missing from the map is
     * treated as empty, which is how a screen excludes a field from matching.
     */
    currentValues: Map<number, string | null>;
    /**
     * Canonical values a rule output is compared against to decide whether it
     * would change anything. Defaults to `currentValues`, and is only passed
     * separately when a screen hides fields from matching (Add Discrepancy on
     * open) yet still has to clear them when a rule blanks them.
     */
    comparisonValues?: Map<number, string | null>;
    /**
     * Whether the rule types matching whatever the values are (no inputs, or
     * only wildcard inputs) take part. They belong to an evaluation filling a
     * form as a whole — a screen opening — not to one reacting to a single
     * field change, where running them would let a change of an unrelated
     * field rewrite their outputs. Defaults to true when no field changed.
     */
    includeUnconditionalRuleTypes?: boolean;
    host: AutomationCascadeHost;
}

/**
 * Dependency graph of the rule types, built once when the rules load.
 *
 * @param fieldIdsSharingTarget - Field ids rendered with the same control as a
 * given one. The evaluation treats them as one value, so the dependency edges
 * — and with them the cycle detection — have to follow them too.
 */
export function buildAutomationCascadeGraph(
    groups: AutomationRuleTypeGroup[],
    fieldIdsSharingTarget?: (fieldId: number) => number[],
): AutomationCascadeGraph {
    const consumersOf = new Map<number, number[]>();
    const producerOf = new Map<number, number>();

    for (const group of groups) {
        for (const fieldId of valueDependentInputFieldIds(group)) {
            const consumers = consumersOf.get(fieldId);
            if (consumers) {
                if (!consumers.includes(group.ruleTypeId))
                    consumers.push(group.ruleTypeId);
            } else {
                consumersOf.set(fieldId, [group.ruleTypeId]);
            }
        }
        for (const fieldId of group.outputFieldIds) {
            producerOf.set(fieldId, group.ruleTypeId);
        }
    }

    const order = topologicalOrder(
        groups,
        consumersOf,
        producerOf,
        fieldIdsSharingTarget,
    );

    return { consumersOf, order: order ?? [], isCyclic: order === null };
}

/**
 * Fields the change of `fieldId` can re-derive, transitively and cycle-safe.
 * Used to clear the manually-edited protection along the whole chain.
 *
 * Reachability follows the declared inputs, exactly as the evaluation
 * schedules its rule types: a field the protection is not cleared for would
 * keep an evaluation the change does reach from writing it.
 *
 * @param fieldIdsSharingTarget - Field ids rendered with the same control as a
 * given one, so a chain running over two rule types that declare their own
 * field for one control is followed as well.
 */
export function downstreamOutputFields(
    groups: AutomationRuleTypeGroup[],
    fieldId: number,
    fieldIdsSharingTarget?: (fieldId: number) => number[],
): number[] {
    const groupsById = new Map(
        groups.map((group) => [group.ruleTypeId, group] as const),
    );
    const consumersOf = declaredConsumersOf(groups);
    const targetsOf = (id: number) => fieldTargetsOf(id, fieldIdsSharingTarget);
    const outputs = new Set<number>();
    const queue = [...targetsOf(fieldId)];
    const seen = new Set<number>(queue);

    while (queue.length > 0) {
        const current = queue.shift() as number;
        for (const ruleTypeId of consumersOf.get(current) ?? []) {
            for (const outputId of groupsById.get(ruleTypeId)?.outputFieldIds ??
                []) {
                for (const targetId of targetsOf(outputId)) {
                    outputs.add(targetId);
                    if (seen.has(targetId)) continue;
                    seen.add(targetId);
                    queue.push(targetId);
                }
            }
        }
    }

    return [...outputs];
}

/**
 * Writes the change of `changedFieldId` produces, in dependency order.
 *
 * Each rule type runs at most once, only after the rule types producing its
 * inputs, and a hard iteration guard bounds the whole cascade so a
 * configuration created before loop validation existed can never loop the
 * browser. A rule type that does not match, whose matching rules conflict, or
 * whose output cannot be used is skipped without affecting the others; a value
 * equal to the current one is not written, but still feeds the rule types
 * consuming it — as does every other field id resolving to the same control.
 *
 * A rule type is reached through the fields it declares as inputs, wildcard
 * ones included: a wildcard input matches any value, but it still ties the
 * rule type to that field, and only a change of one of its fields may rewrite
 * its outputs.
 */
export function evaluateAutomationCascade({
    groups,
    graph,
    changedFieldId,
    currentValues,
    comparisonValues,
    includeUnconditionalRuleTypes,
    host,
}: AutomationCascadeParams): AutomationCascadeWrite[] {
    if (groups.length === 0) return [];
    if (graph.isCyclic) {
        console.warn(
            'Automation cascade skipped: the rule types of this instance form a cycle.',
        );
        return [];
    }

    const groupsById = new Map(
        groups.map((group) => [group.ruleTypeId, group] as const),
    );
    const orderIndexOf = new Map(
        graph.order.map((ruleTypeId, index) => [ruleTypeId, index] as const),
    );
    const values = new Map(currentValues);
    const compared = new Map(comparisonValues ?? currentValues);
    const writes: AutomationCascadeWrite[] = [];
    const visited = new Set<number>();

    // Scheduling follows the declared inputs, which include the fields every
    // rule uses as a wildcard; ordering and cycle detection keep using the
    // graph, where such a field creates no dependency.
    const consumersOf = declaredConsumersOf(groups);

    const targetsOf = (fieldId: number) =>
        fieldTargetsOf(fieldId, host.fieldIdsSharingTarget?.bind(host));

    const unconditionalAncestorsOf = unconditionalAncestorResolver(
        groups,
        targetsOf,
    );

    const changedFieldIds = Array.isArray(changedFieldId)
        ? changedFieldId
        : [changedFieldId];
    const pending = initialRuleTypes({
        groups,
        changedFieldIds,
        consumersOf,
        unconditionalAncestorsOf,
        includeUnconditional:
            includeUnconditionalRuleTypes ?? changedFieldIds.length === 0,
    });

    const enqueueConsumers = (fieldId: number) => {
        for (const ruleTypeId of consumersOf.get(fieldId) ?? []) {
            if (visited.has(ruleTypeId)) continue;
            pending.add(ruleTypeId);
            for (const producer of unconditionalAncestorsOf(ruleTypeId)) {
                if (!visited.has(producer)) pending.add(producer);
            }
        }
    };

    // One pass per rule type is enough: every rule type runs at most once.
    const maxIterations = groups.length;
    let iterations = 0;

    while (pending.size > 0) {
        if (iterations++ >= maxIterations) {
            console.warn(
                'Automation cascade stopped: iteration guard reached, check the rule type configuration of this instance.',
            );
            break;
        }

        const ruleTypeId = takeNext(pending, orderIndexOf);
        visited.add(ruleTypeId);

        const group = groupsById.get(ruleTypeId);
        if (!group) continue;

        // No match and conflicting rules both skip this rule type only: the
        // current output values are preserved and the cascade continues.
        const outputs = host.matchRules(group, values);
        if (!outputs) continue;

        outputs.forEach((ruleValue, fieldId) => {
            if (host.isWriteSuppressed?.(fieldId)) {
                // The manual value stays, but the rest of the chain is still
                // derived from it.
                targetsOf(fieldId).forEach(enqueueConsumers);
                return;
            }

            const prepared = host.prepareWrite(fieldId, ruleValue);
            if (!prepared) return;

            // A value equal to the one already on the form needs no write, but
            // the rest of the chain is still derived from it.
            const unchanged =
                prepared.canonical === (compared.get(fieldId) ?? null);

            // One control can be declared as its own field per rule type, so
            // the value counts as written for each of those field ids.
            const targetIds = targetsOf(fieldId);
            for (const targetId of targetIds) {
                values.set(targetId, prepared.canonical);
                compared.set(targetId, prepared.canonical);
            }

            if (!unchanged) {
                writes.push({
                    fieldId,
                    ruleValue,
                    controlValue: prepared.controlValue,
                });
            }
            targetIds.forEach(enqueueConsumers);
        });
    }

    return writes;
}

/**
 * Input fields whose value a rule type actually matches against: the ones it
 * declares, minus the fields every rule using them uses as a wildcard. A
 * wildcard matches any value, so such a field creates no dependency on the
 * rule type producing it — keeping the edge would order the two rule types
 * needlessly, or report a cycle and skip the whole cascade, for a chain the
 * configuration does not really have.
 */
function valueDependentInputFieldIds(group: AutomationRuleTypeGroup): number[] {
    const wildcard = new Set<number>();
    const concrete = new Set<number>();

    for (const rule of group.rules) {
        for (const input of rule.inputs) {
            (input.isWildcard ? wildcard : concrete).add(input.fieldId);
        }
    }

    return group.inputFieldIds.filter(
        (fieldId) => !wildcard.has(fieldId) || concrete.has(fieldId),
    );
}

/**
 * Rule types an evaluation starts with: the ones the changed fields reach
 * plus, on a whole-form evaluation, the ones matching whatever the values
 * are, which no input change can reach. They are ordered with the rest of the
 * cascade like any other rule type.
 */
function initialRuleTypes({
    groups,
    changedFieldIds,
    consumersOf,
    unconditionalAncestorsOf,
    includeUnconditional,
}: {
    groups: AutomationRuleTypeGroup[];
    changedFieldIds: number[];
    consumersOf: Map<number, number[]>;
    unconditionalAncestorsOf: (ruleTypeId: number) => number[];
    includeUnconditional: boolean;
}): Set<number> {
    const pending = new Set<number>(
        changedFieldIds.flatMap((fieldId) => consumersOf.get(fieldId) ?? []),
    );

    if (includeUnconditional) {
        for (const group of groups) {
            if (matchesUnconditionally(group)) pending.add(group.ruleTypeId);
        }

        return pending;
    }

    for (const ruleTypeId of pending) {
        for (const producer of unconditionalAncestorsOf(ruleTypeId)) {
            pending.add(producer);
        }
    }

    return pending;
}

/**
 * Resolver of the rule types matching whatever the values are that produce,
 * transitively, an input a given rule type depends on the value of.
 *
 * Such a producer is reached by no field change, and the order runs it first.
 * Leaving it out of an evaluation reaching its consumer would let that
 * consumer run on a value the producer then replaces, so it takes part as
 * well — unlike the rule types the evaluation derives nothing for.
 */
function unconditionalAncestorResolver(
    groups: AutomationRuleTypeGroup[],
    targetsOf: (fieldId: number) => number[],
): (ruleTypeId: number) => number[] {
    const groupsById = new Map(
        groups.map((group) => [group.ruleTypeId, group] as const),
    );
    const producersOf = producersByField(groups, targetsOf);
    const resolved = new Map<number, number[]>();

    return (ruleTypeId: number) => {
        const cached = resolved.get(ruleTypeId);
        if (cached) return cached;

        const reached = new Set<number>();
        const queue = [ruleTypeId];

        while (queue.length > 0) {
            const group = groupsById.get(queue.shift() as number);
            if (!group) continue;

            for (const producer of unconditionalProducersFeeding(
                group,
                producersOf,
                groupsById,
            )) {
                if (producer === ruleTypeId || reached.has(producer)) continue;
                reached.add(producer);
                queue.push(producer);
            }
        }

        const ancestors = [...reached];
        resolved.set(ruleTypeId, ancestors);

        return ancestors;
    };
}

/**
 * Rule types matching whatever the values are that produce one of the inputs
 * `group` depends on the value of.
 *
 * Every producer of a control is considered, not only the last one registered
 * for it: two rule types may output to different field ids sharing a control,
 * and an unconditional producer must still be scheduled even when a
 * non-unconditional one also writes that control.
 */
function unconditionalProducersFeeding(
    group: AutomationRuleTypeGroup,
    producersOf: Map<number, number[]>,
    groupsById: Map<number, AutomationRuleTypeGroup>,
): number[] {
    return valueDependentInputFieldIds(group)
        .flatMap((fieldId) => producersOf.get(fieldId) ?? [])
        .filter((ruleTypeId) => {
            if (ruleTypeId === group.ruleTypeId) return false;
            const producer = groupsById.get(ruleTypeId);

            return !!producer && matchesUnconditionally(producer);
        });
}

/**
 * Rule types producing a field, by field id, over the ids of its control.
 * All producers of a control are kept: several rule types may output to field
 * ids sharing one control, and callers need every one of them (an
 * unconditional producer must not be hidden by a later non-unconditional one).
 */
function producersByField(
    groups: AutomationRuleTypeGroup[],
    targetsOf: (fieldId: number) => number[],
): Map<number, number[]> {
    const producersOf = new Map<number, number[]>();

    for (const group of groups) {
        for (const fieldId of group.outputFieldIds) {
            for (const targetId of targetsOf(fieldId)) {
                const producers = producersOf.get(targetId);
                if (!producers) {
                    producersOf.set(targetId, [group.ruleTypeId]);
                } else if (!producers.includes(group.ruleTypeId)) {
                    producers.push(group.ruleTypeId);
                }
            }
        }
    }

    return producersOf;
}

/**
 * Rule types consuming a field as an input, by field id, wildcard inputs
 * included. Unlike `AutomationCascadeGraph.consumersOf` this is about which
 * evaluations a rule type takes part in, not about ordering them: a wildcard
 * input does not make the rule type depend on the value of its field, but it
 * does make a change of that field an evaluation of this rule type.
 */
function declaredConsumersOf(
    groups: AutomationRuleTypeGroup[],
): Map<number, number[]> {
    const consumersOf = new Map<number, number[]>();

    for (const group of groups) {
        for (const fieldId of group.inputFieldIds) {
            const consumers = consumersOf.get(fieldId);
            if (!consumers) {
                consumersOf.set(fieldId, [group.ruleTypeId]);
            } else if (!consumers.includes(group.ruleTypeId)) {
                consumers.push(group.ruleTypeId);
            }
        }
    }

    return consumersOf;
}

/**
 * True when a rule type has a rule matching whatever the current values are:
 * one without inputs, or one whose inputs are all wildcards.
 */
function matchesUnconditionally(group: AutomationRuleTypeGroup): boolean {
    if (group.inputFieldIds.length === 0) return true;

    return group.rules.some((rule) =>
        rule.inputs.every((input) => input.isWildcard),
    );
}

/** Pending rule type that comes first in dependency order. */
function takeNext(
    pending: Set<number>,
    orderIndexOf: Map<number, number>,
): number {
    let next = -1;
    let nextIndex = Number.POSITIVE_INFINITY;

    for (const ruleTypeId of pending) {
        const index = orderIndexOf.get(ruleTypeId) ?? Number.MAX_SAFE_INTEGER;
        if (index < nextIndex) {
            nextIndex = index;
            next = ruleTypeId;
        }
    }

    pending.delete(next);

    return next;
}

/** Kahn's algorithm over "producing rule type -> consuming rule type". */
function topologicalOrder(
    groups: AutomationRuleTypeGroup[],
    consumersOf: Map<number, number[]>,
    producerOf: Map<number, number>,
    fieldIdsSharingTarget?: (fieldId: number) => number[],
): number[] | null {
    const { dependents, incoming } = ruleTypeDependencies(
        groups,
        consumersOf,
        producerOf,
        fieldIdsSharingTarget,
    );

    const ready = [...incoming.entries()]
        .filter(([, count]) => count === 0)
        .map(([ruleTypeId]) => ruleTypeId);
    const order: number[] = [];

    while (ready.length > 0) {
        const ruleTypeId = ready.shift() as number;
        order.push(ruleTypeId);
        for (const consumer of dependents.get(ruleTypeId) ?? []) {
            const remaining = (incoming.get(consumer) ?? 0) - 1;
            incoming.set(consumer, remaining);
            if (remaining === 0) ready.push(consumer);
        }
    }

    return order.length === groups.length ? order : null;
}

/** Edges and in-degrees of the "producing -> consuming rule type" graph. */
function ruleTypeDependencies(
    groups: AutomationRuleTypeGroup[],
    consumersOf: Map<number, number[]>,
    producerOf: Map<number, number>,
    fieldIdsSharingTarget?: (fieldId: number) => number[],
): { dependents: Map<number, Set<number>>; incoming: Map<number, number> } {
    const dependents = new Map<number, Set<number>>();
    const incoming = new Map<number, number>();

    for (const group of groups) {
        dependents.set(group.ruleTypeId, new Set());
        incoming.set(group.ruleTypeId, 0);
    }

    for (const [fieldId, producer] of producerOf) {
        // A produced value reaches the rule types consuming any field id of the
        // same control, so a cycle over those aliases is a cycle here as well.
        for (const targetId of fieldTargetsOf(fieldId, fieldIdsSharingTarget)) {
            for (const consumer of consumersOf.get(targetId) ?? []) {
                if (consumer === producer) continue;
                const edges = dependents.get(producer);
                if (!edges || edges.has(consumer)) continue;
                edges.add(consumer);
                incoming.set(consumer, (incoming.get(consumer) ?? 0) + 1);
            }
        }
    }

    return { dependents, incoming };
}

/** `fieldId` together with the field ids sharing its control. */
function fieldTargetsOf(
    fieldId: number,
    fieldIdsSharingTarget?: (fieldId: number) => number[],
): number[] {
    const ids = fieldIdsSharingTarget?.(fieldId) ?? [];

    return ids.includes(fieldId) ? ids : [fieldId, ...ids];
}
